import { PlusCircle, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { getAllSites } from '../SiteManagement/siteApi';
import { vendorApi } from '../Vender/vendorApi';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input';
import { purchaseOrderApi } from './PurchaseOrderApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const paymentModes = ['Net Banking', 'Bank Transfer', 'UPI', 'Cash', 'Cheque'];

const createEmptyItemRow = () => ({
  name: '',
  hsnCode: '',
  units: '',
  unit: 'pieces', // Unit of measurement (kg, ltr, pieces, etc.)
  rate: '',
  tax: '',
});

const resolveSiteValue = (site) => {
  const value =
    site?.site_id ??
    site?.siteId ??
    site?.id ??
    site?._id ??
    site?.site_code ??
    site?.siteCode;
  return value !== undefined && value !== null ? String(value) : '';
};

const resolveSiteLabel = (site) =>
  site?.site_name ||
  site?.name ||
  site?.siteCode ||
  site?.site_code ||
  `Site ${site?.site_id ?? site?.id ?? ''}`;

const PurchaseOrder = () => {
  // Log screen view
  useScreenLogging('PurchaseOrder', {
    component: 'PurchaseOrder',
    screenType: 'purchase-order',
  });

  const { openOverlay } = useOverlay();
  const navigate = useNavigate();
  const [selectedVendor, setSelectedVendor] = useState('');
  const [poInfo, setPoInfo] = useState({
    poDate: getTodayDate(),
    poExpiryDate: '',
    freightCharges: '',
    expectedDeliveryDate: '',
    paymentDate: '',
    paymentMode: paymentModes[0],
  });
  const [billingAddress, setBillingAddress] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    country: '',
    pinCode: '',
    city: '',
    state: '',
    mobileNumber: '',
    panNumber: '',
    gstNumber: '',
  });
  const [shippingAddress, setShippingAddress] = useState({ ...billingAddress });
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [vendors, setVendors] = useState([]);
  const [isVendorsLoading, setIsVendorsLoading] = useState(false);
  const [vendorsError, setVendorsError] = useState('');
  const [selectedBillingSite, setSelectedBillingSite] = useState('');
  const [selectedShippingSite, setSelectedShippingSite] = useState('');
  const [items, setItems] = useState([createEmptyItemRow()]);
  const [discount, setDiscount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(
            error?.message || 'Unable to load sites. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchVendors = async () => {
      setIsVendorsLoading(true);
      setVendorsError('');
      try {
        const result = await vendorApi.listVendors({ limit: 100, status: 'active' });
        if (result.success && Array.isArray(result.vendors)) {
          const sorted = [...result.vendors].sort((a, b) =>
            (a?.vendor_name || '').localeCompare(b?.vendor_name || '')
          );
          if (isMounted) {
            setVendors(sorted);
          }
        } else {
          if (isMounted) {
            setVendorsError(result.message || 'Unable to load vendors. Please try again later.');
            setVendors([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          setVendorsError(
            error?.message || 'Unable to load vendors. Please try again later.'
          );
          setVendors([]);
        }
      } finally {
        if (isMounted) {
          setIsVendorsLoading(false);
        }
      }
    };

    fetchVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  // Detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    if (useSameAddress) {
      setShippingAddress({ ...billingAddress });
      setSelectedShippingSite((prev) =>
        selectedBillingSite ? selectedBillingSite : prev
      );
    }
  }, [useSameAddress, billingAddress, selectedBillingSite]);

  const handlePOInfoChange = (field, value) => {
    // Validate PO Expiry Date is not earlier than PO Date
    if (field === 'poExpiryDate' && value && poInfo.poDate) {
      if (value < poInfo.poDate) {
        setFeedback({ 
          success: '', 
          error: 'PO Expiry Date cannot be earlier than PO Date.' 
        });
        return;
      }
    }
    
    // If PO Expiry Date is cleared, also clear Expected Delivery Date
    if (field === 'poExpiryDate' && !value) {
      setPoInfo((prev) => ({
        ...prev,
        poExpiryDate: '',
        expectedDeliveryDate: '',
        [field]: value,
      }));
      setFeedback({ success: '', error: '' });
      return;
    }
    
    // Validate Expected Delivery Date is not earlier than PO Date
    if (field === 'expectedDeliveryDate' && value && poInfo.poDate) {
      if (value < poInfo.poDate) {
        setFeedback({ 
          success: '', 
          error: 'Expected Delivery Date cannot be earlier than PO Date.' 
        });
        return;
      }
    }
    
    // If PO Date changes, validate both expiry date and expected delivery date
    if (field === 'poDate' && value) {
      const updates = { [field]: value };
      
      // Clear expiry date if it becomes invalid
      if (poInfo.poExpiryDate && poInfo.poExpiryDate < value) {
        updates.poExpiryDate = '';
        updates.expectedDeliveryDate = '';
        setFeedback({ 
          success: '', 
          error: 'PO Expiry Date cannot be earlier than PO Date. Please update the expiry date.' 
        });
      }
      
      // Clear expected delivery date if it becomes invalid
      if (poInfo.expectedDeliveryDate && poInfo.expectedDeliveryDate < value) {
        updates.expectedDeliveryDate = '';
        if (!updates.poExpiryDate) {
          setFeedback({ 
            success: '', 
            error: 'Expected Delivery Date cannot be earlier than PO Date. Please update the delivery date.' 
          });
        }
      }
      
      if (updates.poExpiryDate === '' || updates.expectedDeliveryDate === '') {
        setPoInfo((prev) => ({
          ...prev,
          ...updates,
        }));
        setFeedback({ success: '', error: '' });
        return;
      }
    }
    
    setPoInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFeedback({ success: '', error: '' });
  };

  const handleAddressChange = (section, field, value) => {
    const updater = section === 'billing' ? setBillingAddress : setShippingAddress;
    updater((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (section === 'billing' && useSameAddress) {
      setShippingAddress((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    setFeedback({ success: '', error: '' });
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setFeedback({ success: '', error: '' });
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, createEmptyItemRow()]);
  };

  const handleClearItem = (index) => {
    setItems((prev) =>
      prev.map((row, idx) => (idx === index ? createEmptyItemRow() : row))
    );
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      handleClearItem(index);
      return;
    }
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSiteSelection = (type, value) => {
    if (type === 'billing') {
      setSelectedBillingSite(value);
      
      // Auto-fill billing address from selected site
      if (value) {
        const selectedSite = sites.find(
          (site) => resolveSiteValue(site) === value
        );
        
        if (selectedSite) {
          // Map site billing address data to form fields
          const updatedBillingAddress = {
            email: selectedSite.billing_email || '',
            firstName: selectedSite.billing_first_name || selectedSite.first_name || '',
            lastName: selectedSite.billing_last_name || selectedSite.last_name || '',
            address: selectedSite.billing_address_line || selectedSite.billing_address || '',
            country: selectedSite.billing_country || '',
            pinCode: selectedSite.billing_pincode || '',
            city: selectedSite.billing_city || '',
            state: selectedSite.billing_state || '',
            mobileNumber: selectedSite.billing_mobile_number || selectedSite.mobile_number || selectedSite.phone || '',
            panNumber: selectedSite.billing_pan_number || selectedSite.pan_number || '',
            gstNumber: selectedSite.billing_gst_number || selectedSite.gst_number || '',
          };
          
          setBillingAddress(updatedBillingAddress);
          
          // If "use same address" is checked, also update shipping address
          if (useSameAddress) {
            setShippingAddress({ ...updatedBillingAddress });
            setSelectedShippingSite(value);
          }
        }
      } else {
        // If no site selected, clear billing address
        setBillingAddress({
          email: '',
          firstName: '',
          lastName: '',
          address: '',
          country: '',
          pinCode: '',
          city: '',
          state: '',
          mobileNumber: '',
          panNumber: '',
          gstNumber: '',
        });
      }
    } else {
      setSelectedShippingSite(value);
      
      // Auto-fill shipping address from selected site
      if (value) {
        const selectedSite = sites.find(
          (site) => resolveSiteValue(site) === value
        );
        
        if (selectedSite) {
          // Map site shipping/billing address data to form fields
          const updatedShippingAddress = {
            email: selectedSite.shipping_email || selectedSite.billing_email || '',
            firstName: selectedSite.shipping_first_name || selectedSite.billing_first_name || selectedSite.first_name || '',
            lastName: selectedSite.shipping_last_name || selectedSite.billing_last_name || selectedSite.last_name || '',
            address: selectedSite.shipping_address_line || selectedSite.shipping_address || selectedSite.billing_address_line || selectedSite.billing_address || '',
            country: selectedSite.shipping_country || selectedSite.billing_country || '',
            pinCode: selectedSite.shipping_pincode || selectedSite.billing_pincode || '',
            city: selectedSite.shipping_city || selectedSite.billing_city || '',
            state: selectedSite.shipping_state || selectedSite.billing_state || '',
            mobileNumber: selectedSite.shipping_mobile_number || selectedSite.billing_mobile_number || selectedSite.mobile_number || selectedSite.phone || '',
            panNumber: selectedSite.shipping_pan_number || selectedSite.billing_pan_number || selectedSite.pan_number || '',
            gstNumber: selectedSite.shipping_gst_number || selectedSite.billing_gst_number || selectedSite.gst_number || '',
          };
          
          setShippingAddress(updatedShippingAddress);
        }
      } else {
        // If no site selected, clear shipping address
        setShippingAddress({
          email: '',
          firstName: '',
          lastName: '',
          address: '',
          country: '',
          pinCode: '',
          city: '',
          state: '',
          mobileNumber: '',
          panNumber: '',
          gstNumber: '',
        });
      }
    }
    setFeedback({ success: '', error: '' });
  };

  const computeRowAmount = (row) => {
    const units = parseFloat(row.units) || 0;
    const rate = parseFloat(row.rate) || 0;
    const tax = parseFloat(row.tax) || 0;
    const base = units * rate;
    const taxValue = base * (tax / 100);
    return base + taxValue;
  };

  const subtotal = useMemo(
    () => items.reduce((sum, row) => sum + computeRowAmount(row), 0),
    [items]
  );

  const discountValue = parseFloat(discount) || 0;
  const grandTotal = Math.max(subtotal - discountValue, 0);

  const siteOptions = useMemo(
    () =>
      sites
        .map((site) => ({
          value: resolveSiteValue(site),
          label: resolveSiteLabel(site),
        }))
        .filter((option) => option.value),
    [sites]
  );

  const isAddressComplete = (address) =>
    !!address.email &&
    !!address.firstName &&
    !!address.lastName &&
    !!address.address &&
    !!address.country &&
    !!address.pinCode &&
    !!address.city &&
    !!address.state &&
    !!address.mobileNumber &&
    !!address.panNumber &&
    !!address.gstNumber;

  const areItemsValid = items.every(
    (row) =>
      row.name.trim().length > 0 &&
      parseFloat(row.units) > 0 &&
      parseFloat(row.rate) > 0 &&
      row.hsnCode.trim().length > 0 &&
      row.unit
  );

  const hasBillingSite = !!selectedBillingSite;
  const hasShippingSite = useSameAddress
    ? !!selectedBillingSite
    : !!selectedShippingSite;

  const isFormValid =
    !!selectedVendor &&
    !!poInfo.poDate &&
    !!poInfo.expectedDeliveryDate &&
    !!poInfo.paymentDate &&
    hasBillingSite &&
    hasShippingSite &&
    isAddressComplete(billingAddress) &&
    (useSameAddress || isAddressComplete(shippingAddress)) &&
    areItemsValid;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid) {
      setFeedback({ success: '', error: 'Please fill all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ success: '', error: '' });
    try {
      // Generate PO number
      const poNumber = `PO-${Date.now().toString().slice(-6)}`;
      
      // Map billing address to API format
      const billingAddressApi = {
        street: billingAddress.address || '',
        city: billingAddress.city || '',
        state: billingAddress.state || '',
        pincode: billingAddress.pinCode || '',
        country: billingAddress.country || '',
        email: billingAddress.email || '',
        first_name: billingAddress.firstName || '',
        last_name: billingAddress.lastName || '',
        mobile_number: billingAddress.mobileNumber || '',
        pan_number: billingAddress.panNumber || '',
        gst_number: billingAddress.gstNumber || '',
      };

      // Map shipping address to API format
      const shippingAddressApi = {
        street: shippingAddress.address || '',
        city: shippingAddress.city || '',
        state: shippingAddress.state || '',
        pincode: shippingAddress.pinCode || '',
        country: shippingAddress.country || '',
        email: shippingAddress.email || '',
        first_name: shippingAddress.firstName || '',
        last_name: shippingAddress.lastName || '',
        mobile_number: shippingAddress.mobileNumber || '',
        pan_number: shippingAddress.panNumber || '',
        gst_number: shippingAddress.gstNumber || '',
      };

      // Helper function to generate SKU from item name
      const generateSku = (itemName) => {
        if (!itemName || !itemName.trim()) {
          return `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }
        // Convert to uppercase, replace spaces and special chars with hyphens, remove multiple hyphens
        return itemName
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
          || `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`; // Fallback if empty
      };

      // Map line items to API format
      const lines = items
        .filter((item) => item.name.trim() && parseFloat(item.units) > 0 && parseFloat(item.rate) > 0)
        .map((item, index) => {
          const quantity = parseFloat(item.units) || 0;
          const unitCost = parseFloat(item.rate) || 0;
          const taxRate = parseFloat(item.tax) || 0;
          const itemName = item.name.trim();

          return {
            line_id: String(index + 1).padStart(3, '0'), // Generate line_id as 001, 002, etc.
            sku: generateSku(itemName), // Generate SKU from item name (required by API)
            description: itemName,
            unit: item.unit || 'pieces',
            quantity: quantity,
            unit_cost: unitCost,
            tax_rate: taxRate > 0 ? taxRate : undefined,
            hsn_code: item.hsnCode.trim() || undefined,
          };
        });

      // Prepare purchase order data for API
      const freightTotal = parseFloat(poInfo.freightCharges) || 0;
      
      // Use billing site as primary site_id, fallback to shipping site if billing site not selected
      const siteId = selectedBillingSite || selectedShippingSite || undefined;
      
      const poData = {
        vendor_id: selectedVendor, // Note: This should be vendor_id, not vendor name. Update when vendor selection uses IDs.
        po_number: poNumber,
        po_date: poInfo.poDate,
        expected_delivery_date: poInfo.expectedDeliveryDate || undefined,
        currency: 'INR',
        site_id: siteId, // Add site_id for filtering
        billing_address: billingAddressApi,
        shipping_address: shippingAddressApi,
        payment_terms: poInfo.paymentMode || undefined,
        freight_total: freightTotal > 0 ? freightTotal : undefined,
        notes: `Billing Site: ${selectedBillingSite}, Shipping Site: ${selectedShippingSite}${poInfo.poExpiryDate ? `, PO Expiry: ${poInfo.poExpiryDate}` : ''}${discountValue > 0 ? `, Discount: ${discountValue}` : ''}`,
        lines: lines,
      };

      console.log('Submitting purchase order:', poData);

      // Call the API
      const result = await purchaseOrderApi.createPurchaseOrder(poData);

      if (result.success) {
        // Prepare purchase order data for preview
        const purchaseOrderData = {
          poNumber: result.purchase_order?.po_number || poNumber,
          selectedVendor,
          poInfo: { ...poInfo },
          billingAddress: { ...billingAddress },
          shippingAddress: { ...shippingAddress },
          useSameAddress,
          selectedBillingSite,
          selectedShippingSite,
          items: items.map((item) => ({
            ...item,
            units: parseFloat(item.units) || 0,
            rate: parseFloat(item.rate) || 0,
            tax: parseFloat(item.tax) || 0,
          })),
          subtotal: result.purchase_order?.subtotal || subtotal,
          discountValue,
          grandTotal: result.purchase_order?.grand_total || grandTotal,
          sites,
          apiResponse: result,
        };

        // Open preview overlay with API response data
        openOverlay('purchaseOrderPrint', { purchaseOrder: purchaseOrderData });
        setFeedback({ 
          success: `Purchase order created successfully! PO Number: ${result.purchase_order?.po_number || poNumber}`, 
          error: '' 
        });
        
        // Navigate to view screen with site and month info for auto-refresh
        const siteId = selectedBillingSite || selectedShippingSite;
        if (siteId) {
          const poDate = result.purchase_order?.po_date || poInfo.poDate;
          let year, month;
          if (poDate && poDate.includes('-')) {
            [year, month] = poDate.split('-');
          } else {
            const now = new Date();
            year = now.getFullYear();
            month = String(now.getMonth() + 1).padStart(2, '0');
          }
          const monthKey = `${year}-${month}`;
          
          // Find the selected site from sites list
          const selectedSiteObj = sites.find(s => 
            resolveSiteValue(s) === siteId
          );
          
          console.log('PurchaseOrder: Navigating to view with state:', {
            siteId,
            month: monthKey,
            poId: result.purchase_order?.po_id,
            poNumber: result.purchase_order?.po_number,
            poDate: poDate,
            note: 'Adding delay to allow backend to commit the purchase order'
          });
          
          // Navigate to view screen after a delay to allow:
          // 1. Overlay to open
          // 2. Backend to commit the purchase order to database
          // Using a longer delay (2 seconds) to account for potential backend eventual consistency
          setTimeout(() => {
            navigate('/onboarding-purchase-order/view', {
              state: {
                siteId: siteId,
                site: selectedSiteObj,
                month: monthKey,
                poId: result.purchase_order?.po_id,
                poNumber: result.purchase_order?.po_number,
                refresh: true,
                justCreated: true, // Flag to indicate this was just created
              }
            });
          }, 2000); // Increased delay to 2 seconds
        }
      } else {
        setFeedback({ 
          success: '', 
          error: result.message || 'Failed to create purchase order. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Failed to create purchase order', error);
      setFeedback({ 
        success: '', 
        error: error.message || 'Failed to create purchase order. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAddressFields = (section, state, disabled = false) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-gray-600">Email Address</label>
          <Input
            type="email"
            value={state.email}
            onChange={(e) => handleAddressChange(section, 'email', e.target.value)}
            placeholder="name@company.com"
            disabled={disabled}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-600">First Name</label>
            <Input
              type="text"
              value={state.firstName}
              onChange={(e) =>
                handleAddressChange(section, 'firstName', e.target.value)
              }
              placeholder="John"
              disabled={disabled}
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">Last Name</label>
            <Input
              type="text"
              value={state.lastName}
              onChange={(e) =>
                handleAddressChange(section, 'lastName', e.target.value)
              }
              placeholder="Doe"
              disabled={disabled}
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
            />
          </div>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm text-gray-600">Address</label>
        <Input
          type="text"
          value={state.address}
          onChange={(e) => handleAddressChange(section, 'address', e.target.value)}
          placeholder="Street, Building, Area"
          disabled={disabled}
          className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-gray-600">Country</label>
          <Input
            type="text"
            value={state.country}
            onChange={(e) => handleAddressChange(section, 'country', e.target.value)}
            placeholder="India"
            disabled={disabled}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-600">Pin Code</label>
          <Input
            type="text"
            value={state.pinCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 10); // Only allow numbers, max 10 digits
              handleAddressChange(section, 'pinCode', value);
            }}
            placeholder="500001"
            disabled={disabled}
            maxLength={10}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="mb-1 block text-sm text-gray-600">City</label>
          <Input
            type="text"
            value={state.city}
            onChange={(e) => handleAddressChange(section, 'city', e.target.value)}
            placeholder="Hyderabad"
            disabled={disabled}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-600">State</label>
          <Input
            type="text"
            value={state.state}
            onChange={(e) => handleAddressChange(section, 'state', e.target.value)}
            placeholder="Telangana"
            disabled={disabled}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-600">Mobile Number</label>
          <Input
            type="tel"
            value={state.mobileNumber}
            onChange={(e) => {
              let value = e.target.value;
              // Allow + at the beginning
              const hasPlus = value.startsWith('+');
              // Remove all non-digit characters except leading +
              const digits = value.replace(/[^\d]/g, '');
              // Limit to 15 digits
              const limitedDigits = digits.slice(0, 15);
              // Reconstruct value with + if it was at the start
              value = hasPlus ? `+${limitedDigits}` : limitedDigits;
              handleAddressChange(section, 'mobileNumber', value);
            }}
            placeholder="+91 9876543210"
            disabled={disabled}
            maxLength={16}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-gray-600">PAN Number</label>
          <Input
            type="text"
            value={state.panNumber}
            onChange={(e) => {
              // Allow only alphanumeric characters, convert to uppercase, limit to 10 characters
              const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
              handleAddressChange(section, 'panNumber', value);
            }}
            placeholder="ABCDE1234F"
            disabled={disabled}
            maxLength={10}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm uppercase"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-600">GST Number</label>
          <Input
            type="text"
            value={state.gstNumber}
            onChange={(e) => {
              // Allow only alphanumeric characters, convert to uppercase, limit to 15 characters
              const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 15);
              handleAddressChange(section, 'gstNumber', value);
            }}
            placeholder="27AABCU9603R1ZM"
            disabled={disabled}
            maxLength={15}
            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm uppercase"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto">
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-gray-900">Purchase Order</h1>
          <p className="mt-2 text-gray-500">
            Create and review purchase order details before submission.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8">
          <section className="grid gap-6 lg:grid-cols-2">
            <div className="flex flex-col gap-6">
              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4">
                  <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Vendor
                    </label>
                    <select
                      className="h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={selectedVendor}
                      onChange={(e) => setSelectedVendor(e.target.value)}
                      disabled={isVendorsLoading}
                    >
                      <option value="" disabled>
                        {isVendorsLoading ? 'Loading vendors...' : 'Select vendor'}
                      </option>
                      {vendors.map((vendor) => (
                        <option key={vendor.vendor_id} value={vendor.vendor_id}>
                          {vendor.vendor_name || vendor.vendor_id}
                        </option>
                      ))}
                    </select>
                    {!isVendorsLoading && !vendors.length && !vendorsError && (
                      <p className="mt-2 text-sm text-gray-500">
                        No vendors available. Please create a vendor first.
                      </p>
                    )}
                    {vendorsError && (
                      <p className="mt-2 text-sm text-red-600">{vendorsError}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">PO Information</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">PO Date</label>
                    <input
                      type="date"
                      value={poInfo.poDate}
                      onChange={(e) => handlePOInfoChange('poDate', e.target.value)}
                      className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      PO Expiry Date
                    </label>
                    <input
                      type="date"
                      value={poInfo.poExpiryDate}
                      min={poInfo.poDate || getTodayDate()}
                      onChange={(e) =>
                        handlePOInfoChange('poExpiryDate', e.target.value)
                      }
                      className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Freight / Delivery Charges
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={poInfo.freightCharges}
                      onChange={(e) =>
                        handlePOInfoChange('freightCharges', e.target.value)
                      }
                      placeholder="0.00"
                      className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Expected Delivery Date
                    </label>
                    <input
                      type="date"
                      value={poInfo.expectedDeliveryDate}
                      min={poInfo.poDate || getTodayDate()}
                      disabled={!poInfo.poExpiryDate}
                      onChange={(e) =>
                        handlePOInfoChange('expectedDeliveryDate', e.target.value)
                      }
                      className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-gray-100"
                    />
                    {!poInfo.poExpiryDate && (
                      <p className="mt-1 text-xs text-gray-500">
                        Please select PO Expiry Date first
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Payment Date
                    </label>
                    <input
                      type="date"
                      value={poInfo.paymentDate}
                      onChange={(e) => handlePOInfoChange('paymentDate', e.target.value)}
                      className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Payment Mode
                    </label>
                    <select
                      value={poInfo.paymentMode}
                      onChange={(e) => handlePOInfoChange('paymentMode', e.target.value)}
                      className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {paymentModes.map((mode) => (
                        <option key={mode} value={mode}>
                          {mode}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="rounded-3xl border border-gray-200 bg-white p-3 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">
                  Select Site (Billing Address)
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Choose the site to map billing details for this purchase order.
                </p>
                <div className="mt-4 space-y-3">
                  <select
                    className="h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={selectedBillingSite}
                    onChange={(e) => handleSiteSelection('billing', e.target.value)}
                  >
                    <option value="">
                      {isSitesLoading ? 'Loading sites...' : 'Select site'}
                    </option>
                    {siteOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {!isSitesLoading && !siteOptions.length && (
                    <p className="mt-2 text-sm text-gray-500">
                      No sites available. Please create a site first.
                    </p>
                  )}
                  {sitesError && (
                    <p className="mt-2 text-sm text-red-600">{sitesError}</p>
                  )}
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 mt-3">
                  <input
                    type="checkbox"
                    checked={useSameAddress}
                    onChange={(e) => setUseSameAddress(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  Use the same address for Shipping Address
                </label>
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-gray-900">
                  Select Site (Shipping Address)
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Choose the site for shipping. This will follow billing if "use the same
                  for shipping address" is selected.
                </p>
                <div className="mt-4">
                  <select
                    className="h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-70"
                    value={selectedShippingSite}
                    onChange={(e) => handleSiteSelection('shipping', e.target.value)}
                    disabled={useSameAddress}
                  >
                    <option value="">
                      {isSitesLoading ? 'Loading sites...' : 'Select site'}
                    </option>
                    {siteOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {useSameAddress && (
                    <p className="mt-2 text-xs text-gray-500">
                      Shipping site matches billing site while the checkbox is enabled.
                    </p>
                  )}
                  {!isSitesLoading && !siteOptions.length && (
                    <p className="mt-2 text-sm text-gray-500">
                      No sites available. Please create a site first.
                    </p>
                  )}
                  {sitesError && (
                    <p className="mt-2 text-sm text-red-600">{sitesError}</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Billing Address</h2>
              </div>
              <div className="mt-4">{renderAddressFields('billing', billingAddress)}</div>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Shipping Address</h2>
              </div>
              <div className="mt-4">
                {renderAddressFields('shipping', shippingAddress, useSameAddress)}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Items</h2>
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-400"
              >
                Add Item
              </button>
            </div>

            {!isMobileView && (
              <div className="mt-4 overflow-x-auto rounded-3xl border border-gray-100">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">Name</th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">HSN Code</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">Unit</th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">Quantity</th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">Rate</th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">Tax (%)</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Amount</th>
                      <th className="px-4 py-3 text-right font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, index) => (
                      <tr key={`po-item-row-${index}`} className="border-t border-gray-100">
                        <td className="px-4 py-4">{index + 1}</td>
                        <td className="px-2 py-2">
                          <Input
                            type="text"
                            value={row.name}
                            onChange={(e) =>
                              handleItemChange(index, 'name', e.target.value)
                            }
                            placeholder="Product name"
                            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="text"
                            value={row.hsnCode}
                            onChange={(e) =>
                              handleItemChange(index, 'hsnCode', e.target.value)
                            }
                            placeholder="HSN Code"
                            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={row.unit || 'pieces'}
                            onChange={(e) =>
                              handleItemChange(index, 'unit', e.target.value)
                            }
                            className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            <option value="pieces">pieces</option>
                            <option value="kg">kg</option>
                            <option value="ltr">ltr</option>
                            <option value="g">g</option>
                            <option value="ml">ml</option>
                            <option value="box">box</option>
                            <option value="pack">pack</option>
                            <option value="unit">unit</option>
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={row.units}
                            onChange={(e) =>
                              handleItemChange(index, 'units', e.target.value)
                            }
                            placeholder="0"
                            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.rate}
                            onChange={(e) =>
                              handleItemChange(index, 'rate', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={row.tax}
                            onChange={(e) => {
                              let value = e.target.value;
                              // Validate and clamp tax value between 0 and 100
                              if (value !== '') {
                                const numValue = parseFloat(value);
                                if (!isNaN(numValue)) {
                                  if (numValue < 0) {
                                    value = '0';
                                  } else if (numValue > 100) {
                                    value = '100';
                                  }
                                }
                              }
                              handleItemChange(index, 'tax', value);
                            }}
                            placeholder="0.00"
                            className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            value={computeRowAmount(row).toFixed(2)}
                            readOnly
                            className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-700"
                          />
                        </td>
                        <td className="px-2 py-2 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              index === 0 ? handleClearItem(index) : handleRemoveItem(index)
                            }
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${
                              index === 0
                                ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                : 'border-red-100 text-red-600 hover:border-red-300'
                            }`}
                          >
                            {index === 0 ? 'Clear' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                      <td colSpan={7} className="px-4 py-3 font-semibold">
                        Total
                      </td>
                      <td className="px-4 py-3">
                        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-right font-semibold">
                          {subtotal.toFixed(2)}
                        </div>
                      </td>
                      <td></td>
                    </tr>
                    <tr className="border-t border-gray-100 bg-white text-gray-700">
                      <td colSpan={7} className="px-4 py-3 font-semibold">
                        Discount (in %)
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={discount}
                          onChange={(e) => setDiscount(e.target.value)}
                          placeholder="0.00"
                          className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm"
                        />
                      </td>
                      <td></td>
                    </tr>
                    <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                      <td colSpan={7} className="px-4 py-3">
                        Grand Total
                      </td>
                      <td className="px-4 py-3">
                        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-right">
                          {grandTotal.toFixed(2)}
                        </div>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {isMobileView && (
              <div
                className="mt-4 space-y-4 rounded-3xl border border-gray-200 bg-white p-2 shadow-sm"
                style={{
                  overscrollBehaviorX: 'none',
                  overflowX: 'hidden',
                  direction: 'ltr'
                }}
              >
                {items.map((row, index) => (
                  <div
                    key={`po-item-card-${index}`}
                    className="rounded-2xl border border-gray-100 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-700">
                        Item #{index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          index === 0 ? handleClearItem(index) : handleRemoveItem(index)
                        }
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          index === 0
                            ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                            : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                        }`}
                      >
                        <XCircle className="h-4 w-4" />
                        {index === 0 ? 'Clear' : 'Remove'}
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Product Name
                        </label>
                        <Input
                          type="text"
                          value={row.name}
                          onChange={(e) =>
                            handleItemChange(index, 'name', e.target.value)
                          }
                          placeholder="Product name"
                          className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          HSN Code
                        </label>
                        <Input
                          type="text"
                          value={row.hsnCode}
                          onChange={(e) =>
                            handleItemChange(index, 'hsnCode', e.target.value)
                          }
                          placeholder="HSN Code"
                          className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Unit
                        </label>
                        <select
                          value={row.unit || 'pieces'}
                          onChange={(e) =>
                            handleItemChange(index, 'unit', e.target.value)
                          }
                          className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          <option value="pieces">pieces</option>
                          <option value="kg">kg</option>
                          <option value="ltr">ltr</option>
                          <option value="g">g</option>
                          <option value="ml">ml</option>
                          <option value="box">box</option>
                          <option value="pack">pack</option>
                          <option value="unit">unit</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Quantity
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={row.units}
                          onChange={(e) =>
                            handleItemChange(index, 'units', e.target.value)
                          }
                          placeholder="0"
                          className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Rate
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.rate}
                          onChange={(e) =>
                            handleItemChange(index, 'rate', e.target.value)
                          }
                          placeholder="0.00"
                          className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Tax (%)
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={row.tax}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Validate and clamp tax value between 0 and 100
                            if (value !== '') {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                if (numValue < 0) {
                                  value = '0';
                                } else if (numValue > 100) {
                                  value = '100';
                                }
                              }
                            }
                            handleItemChange(index, 'tax', value);
                          }}
                          placeholder="0.00"
                          className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Amount
                        </label>
                        <Input
                          type="number"
                          value={computeRowAmount(row).toFixed(2)}
                          readOnly
                          className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-700"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-700">Total</span>
                    <span className="text-sm font-semibold text-gray-800">{subtotal.toFixed(2)}</span>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Discount (in %)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      placeholder="0.00"
                      className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm"
                    />
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-base font-bold text-gray-900">Grand Total</span>
                    <span className="text-base font-bold text-gray-900">{grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Add Item
                  </button>
                </div>
              </div>
            )}
          </section>

          {feedback.error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {feedback.error}
            </div>
          )}
          {feedback.success && (
            <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {feedback.success}
            </div>
          )}

          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Purchase Order'}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseOrder;
